import { MetrikaTheme } from '@metrika/ui-kit';
import * as Sentry from '@sentry/react';
import React, { useEffect, useLayoutEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Route, Switch, useLocation } from 'react-router-dom';
import { appEnv, useSetFlag } from './_env';
import { RiskLazy } from './_risk';
import { RiskError } from './_risk/RiskError';
import { NotFoundRedirect, useIsOfflineService, useLastVersionService } from './_shared';
import { GlobalAppStyle } from './App.styles';
import { useGtmScript } from './gtm';
import { Layout } from './layout-v2';
import { CookieModal, PrivacyLazy, TermsLazy } from './login';
import { UserRoute } from './login/components';
import { SettingsRouteLazy } from './user';
import { useIsLogged, useUserOrg } from './user/hooks';

const App = () => {
   //google tag manager
   useGtmScript();
   /* Recoil Services */
   useLastVersionService();
   useIsOfflineService();

   /* Scrolling / Title reset on page change */
   const { pathname } = useLocation();
   useLayoutEffect(() => {
      window.scrollTo(0, 0);
      // reset title before Helmet - handles stale document title if no title
      document.title = 'Metrika';
   }, [pathname]);

   const setFlag = useSetFlag();
   const org = useUserOrg();
   useEffect(() => {
      if (org?.name === 'metrika') {
         setFlag('debugEnabled', true);
      }
   }, [org, setFlag]);

   const { isLogged, userLoading } = useIsLogged();

   useEffect(() => {
      if (!userLoading && !isLogged) {
         window.location.href = `${appEnv.appDomains.login}/login?return_to=${encodeURIComponent(
            appEnv.appDomains.loginReturnTo + window.location.pathname + window.location.search,
         )}`;
      }
   }, [userLoading, isLogged]);

   return (
      <MetrikaTheme.Provider debug={appEnv.flags.debugLayout}>
         <HelmetProvider>
            <Helmet titleTemplate="Metrika - %s" />
            <GlobalAppStyle />
            <CookieModal />
            <Switch>
               <Layout>
                  <Switch>
                     <Route exact path="/terms" component={TermsLazy} />
                     <Route exact path="/privacy" component={PrivacyLazy} />
                     <UserRoute path="/error" component={RiskError} />
                     <UserRoute path="/risk/settings" component={SettingsRouteLazy} />
                     <UserRoute path="/risk" component={RiskLazy} />
                     <NotFoundRedirect invalid to={'/risk'} />
                  </Switch>
               </Layout>
            </Switch>
         </HelmetProvider>
      </MetrikaTheme.Provider>
   );
};

export default Sentry.withProfiler(App);
